import { Component, Inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { Router } from '@angular/router';
import {
  BookingApi,
  ResourceApi,
  FeatureDTO,
  FindBookingDTO,
  ResourceDTO,
} from '@lkp/parking-sdk';
import { RESOURCE_API } from '../../core/providers/api.module';
import { Actions, Store, ofActionDispatched } from '@ngxs/store';
import {
  Subject,
  debounceTime,
  distinctUntilChanged,
  map,
  takeUntil,
} from 'rxjs';
import { DateService } from 'src/app/core/services/date.service';
import {
  BookingSearchListener,
  GetAllBookings,
} from 'src/app/core/states/bookings/booking.actions';
import { BookingsState } from 'src/app/core/states/bookings/bookings.state';
import dayjs from 'dayjs';

@Component({
    selector: 'app-bookings',
    templateUrl: './bookings.component.html',
    styleUrls: ['./bookings.component.scss'],
    standalone: false
})
export class BookingsComponent {
  bookings: FindBookingDTO[] = [];
  errorMessage = false;
  totalBookings: number = 0;
  displayedColumns: string[] = [
    'bookingNumber',
    'registrationNumber',
    'location',
    'features',
    'flightNumber',
    'departureDate',
    'arrivalDate',
    'passengers',
    'comment',
  ];

  /* Paginator */
  pageIndex: number = 0;
  pageSize: number = 50;
  pageSizeOptions: number[] = [10, 20, 50, 100];

  sortByColumn?: string;
  sortOrder?: 'asc' | 'desc' = 'desc';

  searchAndFilterBookingsForm?: FormGroup;
  showSpinner: boolean = false;

  resources: ResourceDTO[] = [];

  readonly onDestroy$ = new Subject<void>();

  constructor(
    readonly store: Store,
    actions$: Actions,
    readonly fb: FormBuilder,
    readonly dateService: DateService,
    readonly router: Router,
    @Inject(RESOURCE_API) readonly resourceApi: ResourceApi
  ) {
    actions$
      .pipe(
        ofActionDispatched(BookingSearchListener),
        map((action: BookingSearchListener) => action.term),
        debounceTime(300),
        takeUntil(this.onDestroy$)
      )
      .subscribe(() => {
        this.dispatchGetAllBookings();
      });
  }

  ngOnInit() {
    this.store
      .select(BookingsState.allBookings)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((v) => {
        this.bookings = v?.content || [];
        this.totalBookings = v?.totalElements || 0;
      });

    this.resourceApi
      .getResources()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((v) => {
        this.resources = v;
      });

    const now = new Date();
    const oneYearBack = new Date(
      now.getFullYear() - 1,
      now.getMonth(),
      now.getDate()
    );

    /* Create form */
    this.searchAndFilterBookingsForm = this.fb.group({
      from: new FormControl<Date | undefined>(oneYearBack),
      to: new FormControl<Date | undefined>(now),
      location: new FormControl<string>('all'),
      feature: new FormControl<string>('all'),
      filterDateOn: new FormControl<'arrival' | 'departure'>('arrival'),
      searchInput: new FormControl<string | undefined>(undefined),
    });

    this.dispatchGetAllBookings();

    /* Listen to input changes */
    this.searchAndFilterBookingsForm
      ?.get('from')
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.pageIndex = 0;
        this.dispatchGetAllBookings();
      });

    this.searchAndFilterBookingsForm
      ?.get('to')
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.pageIndex = 0;
        this.dispatchGetAllBookings();
      });

    this.searchAndFilterBookingsForm
      ?.get('location')
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.pageIndex = 0;
        this.dispatchGetAllBookings();
      });

    this.searchAndFilterBookingsForm
      ?.get('feature')
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.pageIndex = 0;
        this.dispatchGetAllBookings();
      });

    this.searchAndFilterBookingsForm
      ?.get('filterDateOn')
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.pageIndex = 0;
        this.dispatchGetAllBookings();
      });

    this.searchAndFilterBookingsForm
      ?.get('searchInput')
      ?.valueChanges.pipe(distinctUntilChanged(), takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.showSpinner = true;
        this.pageIndex = 0;
        this.store.dispatch(new BookingSearchListener());
      });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  showBooking(row: any) {
    // open single page view, booking is fetched by the booking page
    this.router.navigate(['bokning', row.bookingId]);
  }

  paginator(event: PageEvent) {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    this.dispatchGetAllBookings();
  }

  sortColumns(event: Sort) {
    this.sortByColumn = event.active;
    this.sortOrder = event.direction || undefined;

    this.dispatchGetAllBookings();
  }

  dispatchGetAllBookings() {
    this.errorMessage = false;
    let location =
      this.searchAndFilterBookingsForm?.controls['location']?.value;
    if (location === 'all') location = undefined;

    let feature = this.searchAndFilterBookingsForm?.controls['feature']?.value;
    if (feature === 'all') feature = undefined;

    /* Search term */
    let searchTerm =
      this.searchAndFilterBookingsForm?.controls['searchInput'].value;

    searchTerm = searchTerm
      ? searchTerm.toString().replace(/\s/g, '')
      : undefined;

    /* Make sure empty strings become undefined */
    searchTerm = searchTerm === '' ? undefined : searchTerm;

    /* Date range */
    let from = this.searchAndFilterBookingsForm?.controls['from'].value;
    let to = this.searchAndFilterBookingsForm?.controls['to'].value;

    if (from && to) {
      const fromDate = dayjs(from).startOf('day').toDate();
      const toDate = dayjs(to).startOf('day').add(1, 'day').toDate();
      this.store
        .dispatch(
          new GetAllBookings(
            this.pageIndex,
            this.pageSize,
            this.sortByColumn,
            this.sortOrder,
            searchTerm,
            fromDate,
            toDate,
            this.searchAndFilterBookingsForm?.controls['filterDateOn'].value ||
              'departure',
            location || undefined,
            feature
          )
        )
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
          next: (res) => {
            this.showSpinner = false;
          },
          error: (error: any) => {
            console.error('Error fetching bookings: ', error);
            this.showSpinner = false;
            this.errorMessage = true;
          },
        });
    }
  }

  formatDate(date: string | Date | undefined): string {
    return date ? this.dateService.formatDate(date) : 'okänt';
  }

  getFlightNumber(booking: FindBookingDTO): string {
    const filterDateOn =
      this.searchAndFilterBookingsForm?.get('filterDateOn')?.value;

    switch (filterDateOn) {
      case 'arrival':
        return booking.arrivalFlightNumber || '-';
      case 'departure':
        return booking.departureFlightNumber || '-';
      default:
        return '-';
    }
  }
}
